import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Inbox, CalendarDays, Mail, Phone, Users, Clock, LogOut, RefreshCw, MessageSquareText, CheckCircle2, CheckCheck, Undo2 } from "lucide-react";
import { EASE } from "@/components/Motion";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "fa_admin_token";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const prettyDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const prettyDate = (d) => {
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch {
    return d;
  }
};

const inputCls = "w-full rounded-2xl border border-sand bg-white/70 px-5 py-3.5 text-[15px] font-light outline-none focus:border-wine transition-colors duration-300";

const Badge = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-sand bg-cream px-3 py-1 text-[11px] tracking-wide text-faded">{children}</span>
);

const STATUS_STYLES = {
  new: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  done: "bg-stone-200 text-stone-600 border-stone-300",
};

const StatusBadge = ({ status }) => (
  <span
    data-testid="admin-status-badge"
    className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-medium tracking-[0.15em] uppercase ${STATUS_STYLES[status] || STATUS_STYLES.new}`}
  >
    {status || "new"}
  </span>
);

const actionBtnCls =
  "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] tracking-[0.12em] uppercase transition-colors duration-300";

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("reservations");
  const [reservations, setReservations] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [r, e] = await Promise.all([
        axios.get(`${API}/api/admin/reservations`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/enquiries`, { headers: authHeaders() }),
      ]);
      setReservations(r.data.items);
      setEnquiries(e.data.items);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      } else {
        setLoadError("Couldn't load your inbox right now — your bookings are safe, please retry.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      if (!localStorage.getItem(TOKEN_KEY)) {
        setUser(false);
        setChecking(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/api/auth/me`, { headers: authHeaders() });
        setUser(data);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      } finally {
        setChecking(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (user && user !== false) loadData();
  }, [user, loadData]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data.user);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const updateStatus = async (kind, id, status) => {
    const setList = kind === "reservations" ? setReservations : setEnquiries;
    setActionError("");
    try {
      await axios.patch(`${API}/api/admin/${kind}/${id}/status`, { status }, { headers: authHeaders() });
      setList((list) => list.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      } else {
        setActionError("Couldn't update the status — please try again.");
      }
    }
  };

  if (checking) {
    return (
      <main className="pt-[72px] min-h-screen flex items-center justify-center" data-testid="admin-page">
        <p className="text-faded text-sm tracking-[0.3em] uppercase animate-pulse">Un momento…</p>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="pt-[72px] min-h-screen flex items-center justify-center px-6" data-testid="admin-page">
        <motion.form
          onSubmit={onLogin}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="w-full max-w-md rounded-[2rem] border border-sand bg-shell p-8 sm:p-10"
          data-testid="admin-login-form"
        >
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-wine text-cream">
            <Inbox size={22} strokeWidth={1.5} />
          </span>
          <h1 className="mt-6 text-center font-serif font-light text-3xl tracking-tight">
            Booking <span className="italic text-wine">Inbox</span>
          </h1>
          <p className="mt-2 text-center text-faded text-sm font-light">Private — for the Forco Amore team only.</p>

          <div className="mt-8 space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Admin email"
              className={inputCls}
              data-testid="admin-email-input"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className={inputCls}
              data-testid="admin-password-input"
            />
          </div>
          {loginError && (
            <p className="mt-4 text-center text-sm text-red-600" data-testid="admin-login-error">{loginError}</p>
          )}
          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-btn"
            className="mt-8 w-full rounded-full bg-wine px-10 py-4 text-sm tracking-[0.16em] uppercase text-cream hover:bg-winedark transition-colors duration-300 disabled:opacity-60"
          >
            {loggingIn ? "Signing in…" : "Sign In"}
          </button>
        </motion.form>
      </main>
    );
  }

  const items = tab === "reservations" ? reservations : enquiries;

  return (
    <main className="pt-[72px] min-h-screen" data-testid="admin-inbox">
      <section className="bg-wine text-cream py-14">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-[11px] tracking-[0.4em] uppercase text-brass">Private</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-3" data-testid="admin-inbox-title">
              Booking <span className="italic text-brass">Inbox</span>
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadData}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-6 py-3 text-xs tracking-[0.14em] uppercase hover:bg-cream hover:text-wine transition-colors duration-300"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-6 py-3 text-xs tracking-[0.14em] uppercase hover:bg-cream hover:text-wine transition-colors duration-300"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </section>

      <div className="border-b border-sand bg-cream/90 sticky top-[72px] z-[60] backdrop-blur-md">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-12 flex gap-2 py-4">
          <button
            onClick={() => setTab("reservations")}
            data-testid="admin-tab-reservations"
            className={`rounded-full px-6 py-2.5 text-[12px] tracking-[0.14em] uppercase transition-all duration-300 ${
              tab === "reservations" ? "bg-wine text-cream" : "border border-sand text-faded hover:text-wine hover:border-wine/40"
            }`}
          >
            Reservations ({reservations.length})
          </button>
          <button
            onClick={() => setTab("enquiries")}
            data-testid="admin-tab-enquiries"
            className={`rounded-full px-6 py-2.5 text-[12px] tracking-[0.14em] uppercase transition-all duration-300 ${
              tab === "enquiries" ? "bg-wine text-cream" : "border border-sand text-faded hover:text-wine hover:border-wine/40"
            }`}
          >
            Enquiries ({enquiries.length})
          </button>
        </div>
      </div>

      <section className="py-12">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-12">
          {loadError && (
            <div className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 p-5" data-testid="admin-load-error">
              <p className="text-sm text-red-700">{loadError}</p>
              <button
                onClick={loadData}
                data-testid="admin-retry-btn"
                className="rounded-full bg-wine px-6 py-2.5 text-xs tracking-[0.14em] uppercase text-cream hover:bg-winedark transition-colors duration-300"
              >
                Retry
              </button>
            </div>
          )}
          {actionError && (
            <p className="mb-6 text-center text-sm text-red-600" data-testid="admin-action-error">{actionError}</p>
          )}
          {items.length === 0 && !loading && !loadError && (
            <p className="text-center text-faded font-light py-20" data-testid="admin-empty-state">
              Nothing here yet — when guests {tab === "reservations" ? "reserve a table" : "send an enquiry"}, it will appear instantly.
            </p>
          )}
          <AnimatePresence mode="popLayout">
            <div className="grid gap-5 md:grid-cols-2">
              {items.map((item) => (
                <motion.article
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="rounded-[1.5rem] border border-sand bg-shell p-6"
                  data-testid={tab === "reservations" ? "admin-reservation-card" : "admin-enquiry-card"}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-serif text-2xl font-medium tracking-tight">{item.name}</h3>
                      <StatusBadge status={item.status} />
                    </div>
                    <span className="text-[11px] text-faded whitespace-nowrap">{prettyDateTime(item.created_at)}</span>
                  </div>

                  {tab === "reservations" ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Badge><CalendarDays size={12} className="text-wine" /> {prettyDate(item.date)}</Badge>
                      <Badge><Clock size={12} className="text-wine" /> {item.time}</Badge>
                      <Badge><Users size={12} className="text-wine" /> {item.guests} {String(item.guests) === "1" ? "guest" : "guests"}</Badge>
                    </div>
                  ) : (
                    <div className="mt-4">
                      <Badge><MessageSquareText size={12} className="text-wine" /> {item.topic}</Badge>
                    </div>
                  )}

                  <div className="mt-4 space-y-1.5 text-sm font-light">
                    <p className="flex items-center gap-2 text-faded"><Phone size={13} /> <a className="hover:text-wine" href={`tel:${(item.phone || "").replace(/\s/g, "")}`}>{item.phone || "—"}</a></p>
                    <p className="flex items-center gap-2 text-faded"><Mail size={13} /> <a className="hover:text-wine" href={`mailto:${item.email}`}>{item.email}</a></p>
                  </div>

                  {(item.requests || item.message) && (
                    <p className="mt-4 rounded-xl bg-cream border border-sand p-4 text-sm font-light leading-relaxed text-ink/80">
                      {item.requests || item.message}
                    </p>
                  )}

                  <div className="mt-5 flex flex-wrap gap-2 border-t border-sand pt-4">
                    {tab === "reservations" ? (
                      <>
                        {(item.status || "new") === "new" && (
                          <button
                            onClick={() => updateStatus("reservations", item.id, "confirmed")}
                            data-testid="admin-confirm-btn"
                            className={`${actionBtnCls} bg-emerald-700 text-white hover:bg-emerald-800`}
                          >
                            <CheckCircle2 size={13} /> Confirm
                          </button>
                        )}
                        {(item.status || "new") !== "done" && (
                          <button
                            onClick={() => updateStatus("reservations", item.id, "done")}
                            data-testid="admin-done-btn"
                            className={`${actionBtnCls} border border-sand text-faded hover:border-wine/40 hover:text-wine`}
                          >
                            <CheckCheck size={13} /> Mark Done
                          </button>
                        )}
                        {(item.status || "new") === "done" && (
                          <button
                            onClick={() => updateStatus("reservations", item.id, "new")}
                            data-testid="admin-reopen-btn"
                            className={`${actionBtnCls} border border-sand text-faded hover:border-wine/40 hover:text-wine`}
                          >
                            <Undo2 size={13} /> Reopen
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        {(item.status || "new") === "new" ? (
                          <button
                            onClick={() => updateStatus("enquiries", item.id, "done")}
                            data-testid="admin-done-btn"
                            className={`${actionBtnCls} bg-emerald-700 text-white hover:bg-emerald-800`}
                          >
                            <CheckCheck size={13} /> Mark Done
                          </button>
                        ) : (
                          <button
                            onClick={() => updateStatus("enquiries", item.id, "new")}
                            data-testid="admin-reopen-btn"
                            className={`${actionBtnCls} border border-sand text-faded hover:border-wine/40 hover:text-wine`}
                          >
                            <Undo2 size={13} /> Reopen
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </motion.article>
              ))}
            </div>
          </AnimatePresence>
        </div>
      </section>
    </main>
  );
}
